import { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import DeviceSettingsSidebar from "./DeviceSettingsSidebar";
import DeviceGraph from "./DeviceGraph";
import BindDeviceSidebar from "./BindDeviceSidebar";
import EntityOperationBar from "../../components/EntityOperationBar";
import {
    BsPencil,
    BsThreeDotsVertical,
    BsChevronDown,
    BsExclamationTriangleFill,
    BsStopwatch,
    BsGear,
} from "react-icons/bs";
import { AiOutlineWifi, AiOutlineClockCircle } from "react-icons/ai";
import { IoLocationSharp } from "react-icons/io5";
import { BiLineChart, BiUnlink, BiPowerOff } from "react-icons/bi";
import { FaBatteryThreeQuarters } from "react-icons/fa";
import { MdSystemUpdateAlt } from "react-icons/md";
import { TbRestore } from "react-icons/tb";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import StarekContext from "../../context/StarekContext";
import { usePermissions } from "../../hooks/usePermissions";
import moment from "moment";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import { getHeartbeatStatus } from "../../utils";
import { softDeleteData, restoreData } from "../../utils/softDelete";
import classNames from "classnames";
import DataTable from "react-data-table-component";
import DeviceCardMenu from "./DeviceCardMenu";
import ConfirmModal from "../../components/modals/ConfirmModal";
import ScheduleMaintenanceModal from "../../components/modals/ScheduleMaintenanceModal";
import { gql, useQuery, useMutation } from "@apollo/client";
import UpdateFirmwareModal from "./UpdateFirmwareModal";
import AddDeviceConfigWizard from "./AddDeviceConfigWizard";
import { Modal } from "react-bootstrap";

// Dialog message constants
const DEVICE_MESSAGES = {
    MAINTENANCE_MARK: (
        deviceId,
    ) => `You are marking device ${deviceId} as under maintenance.

This device will be excluded from plant efficiency calculations until cleared.

Use this when the device requires immediate service or repairs.

Are you sure you wish to continue?`,

    MAINTENANCE_CLEAR: (
        deviceId,
    ) => `You are returning device ${deviceId} to active service by removing its maintenance status.

This device will resume counting towards your plant's operational efficiency metrics.

Are you sure you wish to continue?`,

    UNBIND_SINGLE: (deviceId) => `You are about to unbind device ${deviceId}.

Once unbound, this device will no longer be associated with this workspace.

This action cannot be easily undone. You will need to re-provision and re-bind the device to your workspace.

Are you sure you wish to continue?`,

    UNBIND_BULK: (count) => `You are about to unbind ${count} device(s).

Once unbound, these devices will no longer be associated with this workspace.

This action cannot be easily undone. You will need to re-provision and re-bind each device to your workspace.

Are you sure you wish to continue?`,

    REBOOT_SINGLE: (deviceId) => `You are about to reboot device ${deviceId}.

The device will temporarily go offline while rebooting and will automatically reconnect to the network.

No data will be lost. This is a safe operation.

Are you sure you wish to continue?`,

    REBOOT_BULK: (count) => `You are about to reboot ${count} device(s).

These devices will temporarily go offline while rebooting and will automatically reconnect to the network.

No data will be lost. This is a safe operation.

Are you sure you wish to continue?`,

    FACTORY_RESET_SINGLE: (
        deviceId,
    ) => `You are about to factory reset device ${deviceId}.

This will erase all settings and configurations on this device. Once done, the device will return to factory settings and become unprovisioned.

You will need to completely re-provision the device and re-bind it to your workspace.

Are you sure you wish to continue?`,

    FACTORY_RESET_BULK: (
        count,
    ) => `You are about to factory reset ${count} device(s).

This will erase all settings and configurations on these devices. Once done, they will return to factory settings and become unprovisioned.

You will need to completely re-provision each device and re-bind them to your workspace.

Are you sure you wish to continue?`,

    MAINTENANCE_BULK_ALREADY: (
        total,
        toCreate,
        already,
    ) => `You have selected ${total} devices. ${toCreate} will be marked as under maintenance, while ${already} are already under maintenance (no change will be made for those).

Maintenance devices are excluded from your plant's operational efficiency metrics and will not be assigned new jobs.

Are you sure you wish to continue?`,

    MAINTENANCE_BULK_NEW: (
        count,
    ) => `You are marking ${count} devices as under maintenance.

These devices will be excluded from your plant's operational efficiency calculations until their maintenance status is cleared.

These devices will not be assigned new jobs while under maintenance.

Are you sure you wish to continue?`,
};

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    shadowUrl: markerShadow,
});

const LocationPopup = ({ locationData, onClose }) => {
    const [geographicalName, setGeographicalName] = useState("");
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        if (
            locationData.location?.latitude &&
            locationData.location?.longitude
        ) {
            const fetchGeographicalName = async () => {
                try {
                    const response = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${locationData.location.latitude}&lon=${locationData.location.longitude}`,
                    );
                    const data = await response.json();
                    setGeographicalName(
                        data.display_name || "Unknown Location",
                    );
                } catch (error) {
                    setGeographicalName("Unknown Location");
                } finally {
                    setLoading(false);
                }
            };
            fetchGeographicalName();
        }
    }, [locationData]);

    return (
        <div className="tw-fixed tw-top-0 tw-left-0 tw-w-full tw-h-full tw-bg-gray-800 tw-bg-opacity-50 tw-flex tw-justify-center tw-items-center">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-4 tw-w-[40vw]">
                <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
                    <h3 className="tw-text-lg tw-font-bold">Location</h3>
                    <button
                        className="tw-text-gray-500 tw-font-bold tw-text-xl"
                        onClick={onClose}
                    >
                        &times;
                    </button>
                </div>

                <div className="tw-flex tw-max-w-[38vw] tw-justify-between">
                    <div className="tw-max-w-[15vw]">
                        <p className="tw-text-sm tw-text-gray-600">
                            <strong>IP:</strong>{" "}
                            {locationData.network.address || ""}
                        </p>
                        <p className="tw-text-sm tw-text-gray-600">
                            <strong>Geographical:</strong>{" "}
                            {loading ? "Loading..." : geographicalName}
                        </p>
                        {locationData?.plants?.length > 0 && (
                            <div className="tw-text-sm tw-text-gray-600">
                                <strong>At Plant:</strong>
                                {locationData.plants.map((plant, index) => (
                                    <p className="tw-mt-0 tw-mb-0" key={index}>
                                        - {plant}
                                    </p>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Render Map */}
                    {locationData.location?.latitude &&
                    locationData.location?.longitude ? (
                        <div className="tw-w-[20vw] tw-h-[200px] tw-rounded-lg tw-overflow-hidden">
                            <MapContainer
                                center={[
                                    locationData.location.latitude,
                                    locationData.location.longitude,
                                ]}
                                zoom={13}
                                className="tw-h-[200px] tw-w-full"
                            >
                                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                                <Marker
                                    position={[
                                        locationData.location.latitude,
                                        locationData.location.longitude,
                                    ]}
                                >
                                    <Popup>{geographicalName}</Popup>
                                </Marker>
                            </MapContainer>
                        </div>
                    ) : (
                        <p className="tw-text-sm tw-text-red-500">
                            Location data is missing
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

export const Devices = () => {
    const starekContext = useContext(StarekContext);
    const { can } = usePermissions();
    const canWrite          = can("device", "WRITE");
    const canDestroy        = can("device", "DESTROY");
    const canReboot         = can("device_reboot", "EXECUTE");
    const canUpdateFirmware = can("device_update_firmware", "EXECUTE");

    // Todo: Add refetch of device after successful call of each mutation
    //Todo: Show earliest maintenance first for scheduled maintenance in the maintenance column
    //Todo: There will be only one upcoming maintenance for each device. So, show that only based upon schedule type as 1. So with every new "upcoming maintenance" creation, the previous one will be deleted automatically. You can use schedule_type for checking as it will be one for ADHOC
    //Todo: Toggle in action menu between under maintenance and clear maintenance. Once marked as under maintenance, the action menu should show clear maintenance option. Clear maintenance will remove the device from under maintenance list by deleting ad-hoc maintenance schedule from database
    //Todo: Show list of firmware versions available in the UpdateFirmwareModal and show current version as well by comparing with device.firmware_version. When clicked on any firmware and update then show confirmation modal and based upon confirmation call update firmware gql with required payload to update the firmware of the device
    const GET_DEVICES_IN_PLANT = gql`
        query GetDevicesForPlant($where: ShadowDeviceWhereInput) {
            shadowDevices(where: $where) {
                id
                name
                device
                is_active
                deleted_at
                parameters
                maintenance_schedules {
                    schedule_type
                    schedule_attributes
                }
            }
        }
    `;

    const UPDATE_MANY_SHADOW_DEVICE = gql`
        mutation UpdateManyShadowDevice($data: ShadowDeviceUpdateManyMutationInput!, $where: ShadowDeviceWhereInput) {
            updateManyShadowDevice(data: $data, where: $where) {
                count
            }
        }
    `;

    const CREATE_MAINTENANCE_SCHEDULES = gql`
        mutation CreateManyMaintenanceSchedule(
            $data: [MaintenanceScheduleCreateManyInput!]!
        ) {
            createManyMaintenanceSchedule(data: $data) {
                count
            }
        }
    `;

    const DEVICE_UNBIND = gql`
        mutation DeviceUnbind($deviceId: String!) {
            deviceUnbind(device_id: $deviceId)
        }
    `;

    const DEVICE_COMMAND = gql`
        mutation DeviceCommand(
            $command: String!
            $deviceId: String!
            $parameters: JSONObject
        ) {
            deviceCommand(
                command: $command
                device_id: $deviceId
                parameters: $parameters
            )
        }
    `;

    const DELETE_MAINTENANCE_SCHEDULES = gql`
        mutation deleteManyMaintenanceSchedule(
            $where: MaintenanceScheduleWhereInput!
        ) {
            deleteManyMaintenanceSchedule(where: $where) {
                count
            }
        }
    `;

    const UPDATE_DEVICE_PARAMETERS = gql`
        mutation UpdateDeviceParameters(
            $where: ShadowDeviceWhereUniqueInput!
            $data: ShadowDeviceUpdateInput!
        ) {
            updateOneShadowDevice(where: $where, data: $data) {
                id
                parameters
            }
        }
    `;

    const CREATE_ONE_DEVICE_PROFILE = gql`
        mutation CreateOneDeviceProfile($data: DeviceProfileCreateInput!) {
            createOneDeviceProfile(data: $data) {
                id
                name
                description
                configuration
            }
        }
    `;
    const [showInactive, setShowInactive] = useState(false);

    const {
        data: deviceData,
        loading: deviceLoading,
        error: deviceError,
        refetch,
        startPolling,
        stopPolling,
    } = useQuery(GET_DEVICES_IN_PLANT, {
        variables: {
            where: {
                plant: {
                    is: {
                        id: {
                            equals: starekContext.lastUsedPlant?.id,
                        },
                    },
                },
                ...(showInactive ? {} : { is_active: { equals: true } }),
            },
        },
        // Don't use pollInterval here, we'll control it manually
    });

    const fetchDevices = async () => {
        try {
            await refetch();
        } catch (error) {
            console.error("Error refetching devices");
        }
    };

    const shadowDevices = useMemo(() => {
        return deviceData?.shadowDevices ?? [];
    }, [deviceData?.shadowDevices]);
    // const [searchTerm, setSearchTerm] = useState("");
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState(null);
    const [expandedGraphs, setExpandedGraphs] = useState({});
    const [openBindSidebar, setOpenBindSidebar] = useState(false);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [clearSelectionTick, setClearSelectionTick] = useState(false);
    const [menuOpenForId, setMenuOpenForId] = useState(null);
    const [menuAnchorRect, setMenuAnchorRect] = useState(null);
    const [underMaintenance, setUnderMaintenance] = useState(() => new Set());
    const [fwModal, setFwModal] = useState({ open: false, device: null });
    const [pendingFw, setPendingFw] = useState(null);
    const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
    const [headerMenuAnchor, setHeaderMenuAnchor] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const [scheduleCfg, setScheduleCfg] = useState({
        open: false,
        targetIds: [],
    });
    const [configWizard, setConfigWizard] = useState({ open: false, device: null });

    // Use ref to track expanded graphs for mutation callback to avoid closure issues
    const expandedGraphsRef = useRef(expandedGraphs);
    useEffect(() => {
        expandedGraphsRef.current = expandedGraphs;
    }, [expandedGraphs]);

    const [createManyMaintenance, { loading: creatingMaint }] = useMutation(
        CREATE_MAINTENANCE_SCHEDULES,
        { onCompleted: () => fetchDevices() },
    );

    const [deviceUnbindMutation] = useMutation(DEVICE_UNBIND, {
        onCompleted: () => fetchDevices(),
    });
    const [deviceCommandMutation] = useMutation(DEVICE_COMMAND, {
        onCompleted: () => {
            // Only refresh device list if no graphs are expanded
            // Use ref to avoid stale closure
            const hasExpandedGraphs = Object.values(
                expandedGraphsRef.current,
            ).some((val) => val === true);
            if (!hasExpandedGraphs) {
                fetchDevices();
            }
        },
    });
    const [deleteManyMaintenance] = useMutation(DELETE_MAINTENANCE_SCHEDULES, {
        onCompleted: () => fetchDevices(),
    });
    const [updateDeviceParameters] = useMutation(UPDATE_DEVICE_PARAMETERS, {
        onCompleted: () => fetchDevices(),
    });
    const [createDeviceProfile] = useMutation(CREATE_ONE_DEVICE_PROFILE);
    const [updateManyShadowDevice] = useMutation(UPDATE_MANY_SHADOW_DEVICE, {
        client: starekContext.engineClient,
    });

    const hasSelection = selectedRows.length > 1;

    const openConfirm = (cfg) => setConfirmCfg({ open: true, ...cfg });
    const closeConfirm = () => setConfirmCfg({ open: false });
    const openSchedule = (ids) =>
        setScheduleCfg({ open: true, targetIds: ids });
    const closeSchedule = () => setScheduleCfg({ open: false, targetIds: [] });
    const TYPE_ADHOC =
        starekContext?.types?.maintenance_schedules?.schedule_types?.ADHOC ?? 1;
    const TYPE_SCHEDULED =
        starekContext?.types?.maintenance_schedules?.schedule_types
            ?.SCHEDULED ?? 2;

    // Memoize whether any graphs are expanded to prevent unnecessary re-renders
    const hasExpandedGraphs = useMemo(() => {
        return Object.values(expandedGraphs).some((val) => val === true);
    }, [expandedGraphs]);

    // Control device list polling based on whether any graphs are expanded
    useEffect(() => {
        if (hasExpandedGraphs) {
            // Stop polling when graphs are expanded
            stopPolling();
        } else {
            // Start polling every 60 seconds when no graphs are expanded
            startPolling(60000);
        }

        return () => {
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasExpandedGraphs]); // Only depend on hasExpandedGraphs, not the functions

    useEffect(() => {
        const devices = deviceData?.shadowDevices ?? [];
        const next = new Set();
        devices.forEach((d) => {
            if (Array.isArray(d?.maintenance_schedules)) {
                if (
                    d.maintenance_schedules.some(
                        (m) => m?.schedule_type === TYPE_ADHOC,
                    )
                ) {
                    next.add(d.id);
                }
            }
        });

        // Only update if the set actually changed to prevent infinite loops
        const currentIds = Array.from(underMaintenance).sort().join(",");
        const nextIds = Array.from(next).sort().join(",");
        if (currentIds !== nextIds) {
            setUnderMaintenance(next);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceData, TYPE_ADHOC]); // Use deviceData directly which is stable from Apollo

    useEffect(() => {
        if (selectedRows.length > 1) {
            setMenuOpenForId(null);
        }
    }, [selectedRows]);

    const runCreateManyInChunks = async (rows, chunkSize = 50) => {
        const chunks = [];
        for (let i = 0; i < rows.length; i += chunkSize) {
            chunks.push(rows.slice(i, i + chunkSize));
        }
        await Promise.all(
            chunks.map((data) =>
                createManyMaintenance({ variables: { data } }),
            ),
        );
    };

    const unbindDevice = async ({ deviceId }) => {
        await deviceUnbindMutation({ variables: { deviceId } });
        return true;
    };

    const getEarliestScheduled = (row, TYPE_SCHEDULED) => {
        const list = Array.isArray(row?.maintenance_schedules)
            ? row.maintenance_schedules
            : [];
        const now = Date.now();
        const scheduled = list.filter(
            (m) => m?.schedule_type === TYPE_SCHEDULED,
        );
        if (scheduled.length === 0) return null;

        const withFrom = scheduled
            .map((m) => ({
                ...m,
                __from: new Date(m?.schedule_attributes?.from || 0).getTime(),
            }))
            .filter((m) => Number.isFinite(m.__from) && m.__from > now);
        return withFrom.length > 0
            ? withFrom.sort((a, b) => a.__from - b.__from)[0]
            : null;
    };

    const getMaintenanceStatus = (row, TYPE_ADHOC, TYPE_SCHEDULED) => {
        const list = Array.isArray(row?.maintenance_schedules)
            ? row.maintenance_schedules
            : [];

        const adhoc = list.find((m) => m?.schedule_type === TYPE_ADHOC);
        if (adhoc) {
            return {
                type: "adhoc",
                data: adhoc,
                display: "Under Maintenance",
            };
        }

        const upcoming = getEarliestScheduled(row, TYPE_SCHEDULED);
        if (upcoming) {
            const fromISO = upcoming?.schedule_attributes?.from;
            const from = fromISO ? moment.utc(fromISO) : null;
            const diffMs = from ? from.diff(moment.utc()) : null;
            const human =
                diffMs != null
                    ? diffMs > 0
                        ? `in ${moment.duration(diffMs).humanize()}`
                        : `${moment.duration(-diffMs).humanize()} ago`
                    : "";
            return {
                type: "scheduled",
                data: upcoming,
                display: `Next Due: ${
                    from ? from.format("MMM DD, YYYY") : "Not scheduled"
                }`,
                humanized: human,
            };
        }

        // Fallback: No maintenance
        return {
            type: "none",
            display: "All Good",
        };
    };

    const sendDeviceCommand = async ({
        deviceId,
        command,
        parameters = {},
    }) => {
        await deviceCommandMutation({
            variables: { command, deviceId, parameters },
        });
        return true;
    };
    const openFirmwareModal = (deviceRow) =>
        setFwModal({ open: true, device: deviceRow });

    const doUnderMaintenance = async (deviceIds) => {
        const plantId = starekContext?.lastUsedPlant?.id ?? null;
        const user_Id = Number(starekContext?.user?.id);
        if (!user_Id) {
            throw new Error(
                "No logged-in user id (created_by_id/updated_by_id required).",
            );
        }

        const already = deviceIds.filter((id) => underMaintenance.has(id));
        const toCreate = deviceIds.filter((id) => !underMaintenance.has(id));

        if (toCreate.length === 0) {
            starekContext.showSuccess?.(
                `${already.length} device(s) already Under Maintenance. Nothing to change.`,
            );
            return;
        }
        await deleteManyMaintenance({
            variables: {
                where: {
                    device_id: { in: toCreate },
                    schedule_type: { equals: TYPE_ADHOC },
                },
            },
        });

        const nowISO = new Date().toISOString();
        const rows = toCreate.map((id) => ({
            name: `Adhoc Maintenance - ${id} - ${nowISO}`,
            plant_id: plantId,
            schedule_type: TYPE_ADHOC,
            schedule_attributes: { from: nowISO },
            device_id: id,
            created_by_id: user_Id,
            updated_by_id: user_Id,
        }));

        try {
            await runCreateManyInChunks(rows);
            setUnderMaintenance((prev) => {
                const next = new Set(prev);
                toCreate.forEach((id) => next.add(id));
                return next;
            });
            starekContext.showSuccess?.(
                `Marked ${toCreate.length} device(s) Under Maintenance. ` +
                    (already.length
                        ? `${already.length} already Under Maintenance (left unchanged).`
                        : ""),
            );
        } catch (e) {
            starekContext.showError?.(
                e.message || "Failed to mark Under Maintenance",
            );
        }
    };

    const doClearUnderMaintenance = async (deviceIds) => {
        try {
            await deleteManyMaintenance({
                variables: {
                    where: {
                        device_id: { in: deviceIds },
                        schedule_type: { equals: TYPE_ADHOC },
                    },
                },
            });
            setUnderMaintenance((prev) => {
                const next = new Set(prev);
                deviceIds.forEach((id) => next.delete(id));
                return next;
            });
            starekContext.showSuccess?.(
                `Cleared Under Maintenance for ${deviceIds.length} device(s).`,
            );
        } catch (e) {
            starekContext.showError?.(
                e?.message || "Failed to clear Under Maintenance",
            );
        }
    };

    const doUnbind = async (ids) => {
        let ok = 0,
            fail = 0,
            firstErr = null;
        for (const id of ids) {
            try {
                await unbindDevice({ deviceId: id });
                ok++;
            } catch (e) {
                fail++;
                firstErr = firstErr || e;
            }
        }
        if (fail)
            starekContext.showError?.(
                `Unbound ${ok}/${ids.length}. ${
                    firstErr?.message || "Some requests failed."
                }`,
            );
        else
            starekContext.showSuccess?.(
                `Unbound ${ok}/${ids.length} device(s).`,
            );
        await fetchDevices();
    };

    // TODO: Add loading state when response has not been received yet
    const doCommand = async (ids, command, parameters = {}) => {
        let ok = 0,
            fail = 0,
            firstErr = null;
        for (const id of ids) {
            try {
                await sendDeviceCommand({ deviceId: id, command, parameters });
                ok++;
            } catch (e) {
                fail++;
                firstErr = firstErr || e;
            }
        }
        if (fail)
            starekContext.showError?.(
                `Sent '${command}' to ${ok}/${ids.length}. ${
                    firstErr?.message || "Some requests failed."
                }`,
            );
        else
            starekContext.showSuccess?.(
                `Sent '${command}' to ${ok}/${ids.length} device(s).`,
            );

        // Only refetch devices if no graphs are expanded to avoid re-rendering
        const hasExpandedGraphs = Object.values(expandedGraphs).some(
            (val) => val === true,
        );
        if (!hasExpandedGraphs) {
            await fetchDevices();
        }
    };

    const doReboot = async (ids) => {
        await doCommand(ids, "reboot", {
            reason: "portal-initiated",
            delay: 30000,
        });
        starekContext.showSuccess?.(
            `Reboot command sent to ${ids.length} device(s).`,
        );
    };
    const doFactoryReset = async (ids) => {
        await doCommand(ids, "reset_device", { reason: "portal-initiated" });
        starekContext.showSuccess?.(
            `Factory reset queued for ${ids.length} device(s).`,
        );
    };

    const doScheduleMaintenance = async (deviceIds, fromISO, toISO) => {
        const plantId = starekContext?.lastUsedPlant?.id ?? null;

        const user_Id = Number(starekContext?.user?.id);
        if (!user_Id) {
            throw new Error(
                "No logged-in user id found (created_by_id/updated_by_id required).",
            );
        }
        //TODO: Need to discuss about Scheduled Maintenance Name
        const rows = deviceIds.map((id) => ({
            name: `Scheduled Maintenance (${new Date(
                fromISO,
            ).toISOString()} → ${new Date(toISO).toISOString()})`,
            plant_id: plantId,
            schedule_type: TYPE_SCHEDULED,
            schedule_attributes: { from: fromISO, to: toISO },
            device_id: id,
            created_by_id: user_Id,
            updated_by_id: user_Id,
        }));

        try {
            await runCreateManyInChunks(rows);
            starekContext.showSuccess?.(
                `Maintenance scheduled for ${deviceIds.length} device(s).`,
            );
        } catch (e) {
            starekContext.showError?.(
                e.message || "Failed to schedule maintenance",
            );
        }
    };

    const customStyles = {
        table: { style: { overflow: "visible" } },
        tableWrapper: { style: { overflow: "visible" } },
        headRow: { style: { overflow: "visible" } },
        headCells: { style: { overflow: "visible" } },
        rows: { style: { overflow: "visible" } },
        cells: { style: { overflow: "visible", position: "relative" } },
    };

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return shadowDevices;
        return shadowDevices.filter((item) => {
            const fields = [
                item?.name,
                item?.id,
                item?.device?.model,
                item?.device?.firmware_version,
            ];
            return fields.some((v) =>
                (v ?? "").toString().toLowerCase().includes(q),
            );
        });
    }, [shadowDevices, filterText]);

    const columns = [
        {
            name: "Status",
            width: "120px",
            sortable: false,
            cell: (row) => {
                const heartbeatStatus = getHeartbeatStatus(
                    row.device.lastSeen.master,
                );
                const batteryLevel =
                    row.device.runtime.attributes.battery_level || 0;

                return (
                    <div className="tw-flex tw-items-center tw-justify-center tw-gap-2">
                        <AiOutlineWifi
                            className={`tw-text-lg ${
                                heartbeatStatus.style.includes("success")
                                    ? "tw-text-green-500"
                                    : heartbeatStatus.style.includes("warning")
                                      ? "tw-text-yellow-500"
                                      : "tw-text-red-500"
                            }`}
                            size={18}
                            title={`Connection: ${heartbeatStatus.label}`}
                        />
                        <IoLocationSharp
                            className="tw-text-blue-500 tw-cursor-pointer hover:tw-text-blue-700 tw-text-lg"
                            onClick={() =>
                                handleLocationClick(
                                    row.device.runtime.attributes,
                                )
                            }
                            size={18}
                            title="View Location"
                        />
                        <div
                            className="tw-flex tw-items-center"
                            title={`Battery: ${batteryLevel}%`}
                        >
                            <FaBatteryThreeQuarters
                                className={`tw-text-lg ${
                                    batteryLevel > 75
                                        ? "tw-text-green-500"
                                        : batteryLevel > 25
                                          ? "tw-text-yellow-500"
                                          : "tw-text-red-500"
                                }`}
                                size={18}
                            />
                        </div>
                    </div>
                );
            },
            ignoreRowClick: true,
        },
        {
            name: "Device",
            selector: (row) => row.name || row.id,
            sortable: true,
            cell: (row) => {
                return (
                    <div className="tw-flex tw-flex-col tw-py-2">
                        <div className="tw-flex tw-items-center tw-mb-1">
                            <span className="tw-font-bold tw-text-gray-800">
                                {row.name || row.id}
                            </span>
                        </div>
                        <div className="tw-text-sm tw-text-gray-500 tw-mb-2">
                            {row.device.model
                                ? `[${row.device.model}]`
                                : "Model"}
                        </div>
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <div className="tw-flex tw-items-center">
                                <span className="tw-text-xs tw-text-gray-400">
                                    FW:
                                </span>
                                <span className="tw-text-xs tw-text-gray-600 tw-ml-1">
                                    {row.device.firmware_version || ""}
                                </span>
                            </div>
                            {!row.device.controlledOffline && (
                                <BiLineChart
                                    className={`tw-cursor-pointer tw-p-1 ${
                                        expandedGraphs[row.id]
                                            ? "tw-text-white tw-bg-blue-500 tw-rounded-sm"
                                            : "tw-text-blue-500"
                                    }`}
                                    onClick={(e) => {
                                        e.stopPropagation(); // Prevent row selection
                                        toggleGraph(row.id);
                                    }}
                                    size={16}
                                    title="Toggle Graph View"
                                />
                            )}
                            <BsPencil
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent row selection
                                    handleSettingsClick(row);
                                }}
                                className="tw-text-blue-500 tw-cursor-pointer hover:tw-text-blue-700"
                                size={12}
                                title="Device Settings"
                            />
                        </div>
                    </div>
                );
            },
        },
        {
            name: "Last Seen",
            sortable: true,
            cell: (row) => {
                if (row.device.controlledOffline) {
                    return (
                        <div className="tw-text-sm tw-text-gray-600">
                            Waiting to reconnect
                        </div>
                    );
                }
                const lastSeenTimestamp = row.device.lastSeen.master;
                const lastSeenMoment = moment(lastSeenTimestamp);
                const heartbeatStatus = getHeartbeatStatus(lastSeenTimestamp);

                return (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <div className="tw-text-sm tw-font-medium tw-text-gray-800">
                            {lastSeenMoment.format("MMM DD, YYYY")}
                        </div>
                        <div className="tw-text-xs tw-text-gray-500 tw-mb-1">
                            {lastSeenMoment.format("hh:mm A")}
                        </div>
                        <div className="tw-flex tw-items-center">
                            <span
                                className={classNames(
                                    "tw-px-2 tw-py-1 tw-rounded tw-text-xs",
                                    heartbeatStatus.style,
                                )}
                            >
                                {heartbeatStatus.label}
                            </span>
                        </div>
                    </div>
                );
            },
        },
        {
            name: "Job",
            cell: (row) => {
                if (row.device.controlledOffline) {
                    return (
                        <div className="tw-text-sm tw-text-gray-600">
                            {row.device.lastCommand?.command || "No command"}
                        </div>
                    );
                }
                return (
                    <div className="tw-flex tw-flex-col tw-gap-1">
                        <div className="tw-text-sm tw-font-medium tw-text-gray-800">
                            {row.name || "No Assigned Jobs"}
                        </div>
                        {row.name && (
                            <div className="tw-flex tw-items-center">
                                <span className="tw-bg-blue-100 tw-text-blue-800 tw-text-xs tw-px-2 tw-py-1 tw-rounded">
                                    {row.completed_cycles || 0} Cycles
                                </span>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            name: "Maintenance",
            cell: (row) => {
                if (row.device.controlledOffline) {
                    return (
                        <div className="tw-text-sm tw-text-gray-600">
                            {row.device.lastCommand?.parameters?.reason ||
                                "No reason"}
                        </div>
                    );
                }
                const status = getMaintenanceStatus(
                    row,
                    TYPE_ADHOC,
                    TYPE_SCHEDULED,
                );

                if (status.type === "adhoc") {
                    return (
                        <div className="tw-flex tw-flex-col">
                            <div className="tw-text-sm tw-font-medium tw-text-orange-600">
                                {status.display}
                            </div>
                            <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                This station is not available for jobs.
                            </div>
                        </div>
                    );
                }

                if (status.type === "scheduled") {
                    return (
                        <div className="tw-flex tw-flex-col">
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <BsExclamationTriangleFill
                                    className="tw-text-yellow-500"
                                    size={16}
                                />
                                <div className="tw-text-sm tw-font-medium tw-text-yellow-700">
                                    {status.display}
                                </div>
                            </div>
                            <div className="tw-text-xs tw-text-gray-500 tw-mt-1">
                                {status.humanized}
                            </div>
                        </div>
                    );
                }

                return (
                    <div className="tw-flex tw-flex-col">
                        <div className="tw-text-sm tw-font-medium tw-text-green-600">
                            {status.display}
                        </div>
                    </div>
                );
            },
        },
        {
            name: "Actions",
            width: "80px",
            sortable: false,
            ignoreRowClick: true,
            cell: (row) => {
                if (row.device.controlledOffline) {
                    return null;
                }
                if (!canWrite && !canDestroy && !canReboot && !canUpdateFirmware) {
                    return null;
                }
                return (
                    <div className="tw-w-full tw-flex tw-justify-center">
                        <div className="tw-relative">
                            <button
                                type="button"
                                className={classNames(
                                    "tw-p-1 tw-rounded",
                                    hasSelection
                                        ? "tw-opacity-50 tw-cursor-not-allowed"
                                        : "hover:tw-bg-gray-100",
                                )}
                                aria-label="Actions"
                                title={
                                    hasSelection
                                        ? "Row actions are disabled when multiple rows are selected"
                                        : "Actions"
                                }
                                disabled={hasSelection}
                                onMouseDown={(e) => {
                                    e.stopPropagation();
                                    if (hasSelection) {
                                        e.preventDefault();
                                    }
                                }}
                                onClick={(e) => {
                                    if (hasSelection) return;
                                    e.stopPropagation();
                                    setMenuAnchorRect(
                                        e.currentTarget.getBoundingClientRect(),
                                    );
                                    setMenuOpenForId((prev) =>
                                        prev === row.id ? null : row.id,
                                    );
                                }}
                            >
                                <BsThreeDotsVertical
                                    className={classNames(
                                        hasSelection
                                            ? "tw-text-gray-300"
                                            : "tw-text-gray-600",
                                    )}
                                />
                            </button>

                            {menuOpenForId === row.id && (
                                <DeviceCardMenu
                                    anchorRect={menuAnchorRect}
                                    isUnderMaintenance={underMaintenance.has(
                                        row.id,
                                    )}
                                    onAddMaintenance={() => {
                                        setMenuOpenForId(null);
                                        openSchedule([row.id]);
                                    }}
                                    onUnderMaintenance={() => {
                                        setMenuOpenForId(null);
                                        const isClear = underMaintenance.has(
                                            row.id,
                                        );
                                        const deviceId = row.name || row.id;
                                        openConfirm({
                                            title: isClear
                                                ? "Return Device to Service?"
                                                : "Mark Device for Maintenance?",
                                            message: isClear
                                                ? DEVICE_MESSAGES.MAINTENANCE_CLEAR(
                                                      deviceId,
                                                  )
                                                : DEVICE_MESSAGES.MAINTENANCE_MARK(
                                                      deviceId,
                                                  ),
                                            confirmText: isClear
                                                ? "Return to Service"
                                                : "Mark Maintenance",
                                            cancelText: "Cancel",
                                            type: "warning",
                                            count: 1,
                                            onConfirm: async () => {
                                                if (isClear) {
                                                    await doClearUnderMaintenance(
                                                        [row.id],
                                                    );
                                                } else {
                                                    await doUnderMaintenance([
                                                        row.id,
                                                    ]);
                                                }
                                                closeConfirm();
                                            },
                                        });
                                    }}
                                    onConfigure={() => {
                                        setMenuOpenForId(null);
                                        setConfigWizard({ open: true, device: row });
                                    }}
                                    onUnbind={() => {
                                        setMenuOpenForId(null);
                                        const deviceId = row.name || row.id;
                                        openConfirm({
                                            title: "Unbind Device?",
                                            message:
                                                DEVICE_MESSAGES.UNBIND_SINGLE(
                                                    deviceId,
                                                ),
                                            type: "danger",
                                            confirmText: "Unbind",
                                            count: 1,
                                            onConfirm: async () => {
                                                await doUnbind([row.id]);
                                                closeConfirm();
                                            },
                                        });
                                    }}
                                    onReboot={() => {
                                        setMenuOpenForId(null);
                                        const deviceId = row.name || row.id;
                                        openConfirm({
                                            title: "Reboot confirmation?",
                                            message:
                                                DEVICE_MESSAGES.REBOOT_SINGLE(
                                                    deviceId,
                                                ),
                                            confirmText: "Reboot",
                                            count: 1,
                                            onConfirm: async () => {
                                                await doReboot([row.id]);
                                                closeConfirm();
                                            },
                                        });
                                    }}
                                    onUpdateFirmware={() => {
                                        setMenuOpenForId(null);
                                        openFirmwareModal(row);
                                    }}
                                    onFactoryReset={() => {
                                        setMenuOpenForId(null);
                                        const deviceId = row.name || row.id;
                                        openConfirm({
                                            title: "Factory reset confirmation?",
                                            message:
                                                DEVICE_MESSAGES.FACTORY_RESET_SINGLE(
                                                    deviceId,
                                                ),
                                            type: "danger",
                                            confirmText: "Reset",
                                            count: 1,
                                            onConfirm: async () => {
                                                await doFactoryReset([row.id]);
                                                closeConfirm();
                                            },
                                        });
                                    }}
                                    onClose={() => setMenuOpenForId(null)}
                                />
                            )}
                        </div>
                    </div>
                );
            },
        },
    ];
    //   const [visibleColumns, setVisibleColumns] = useState(columns);

    const handleLocationClick = (location) => {
        setSelectedLocation(location);
    };
    const handleClosePopup = () => {
        setSelectedLocation(null);
    };
    const handleSettingsClick = (device) => {
        setSelectedDevice(device);
        setIsSidebarOpen(true);
    };
    const lastUsedWorkspace = starekContext.lastUsedWorkspace;
    const toggleGraph = (deviceId) => {
        setExpandedGraphs((prev) => {
            const newState = {};
            // Collapse all other expanded graphs
            Object.keys(prev).forEach((key) => {
                newState[key] = false;
            });
            // Toggle the current device graph
            newState[deviceId] = !prev[deviceId];
            return newState;
        });
    };
    const initiateBinding = () => {
        if (starekContext.hasReachedLimit("devices")) {
            starekContext.showWarning(
                `Device limit reached. Your current plan allows up to ${starekContext.subscription?.limits?.devices} devices. Please upgrade to add more.`
            );
            return;
        }
        setOpenBindSidebar(true);
    };

    // Extended actions for bulk device operations
    const extendedActions = useMemo(() => {
        const actions = [];

        if (canWrite) {
            actions.push({
                label: "Add Scheduled Maintenance",
                icon: <AiOutlineClockCircle size={18} />,
                onClick: (rows) => {
                    openSchedule(rows.map((r) => r.id));
                },
                withDivider: true,
            });
            actions.push({
                label:
                    underMaintenance.size > 0
                        ? "Clear Maintenance"
                        : "Under Maintenance",
                icon: <BsStopwatch size={18} />,
                onClick: (rows) => {
                    const ids = rows.map((r) => r.id);
                    const already = ids.filter((id) =>
                        underMaintenance.has(id),
                    ).length;
                    const toCreate = ids.length - already;

                    openConfirm({
                        title: "Mark Devices for Maintenance?",
                        message:
                            already > 0
                                ? DEVICE_MESSAGES.MAINTENANCE_BULK_ALREADY(
                                      ids.length,
                                      toCreate,
                                      already,
                                  )
                                : DEVICE_MESSAGES.MAINTENANCE_BULK_NEW(
                                      ids.length,
                                  ),
                        confirmText: "Mark Maintenance",
                        cancelText: "Cancel",
                        type: "warning",
                        count: ids.length,
                        onConfirm: async () => {
                            await doUnderMaintenance(ids);
                            setClearSelectionTick((t) => !t);
                            closeConfirm();
                        },
                    });
                },
                withDivider: true,
            });
            actions.push({
                label: "Configure",
                icon: <BsGear size={18} />,
                onClick: (rows) => {
                    if (rows.length !== 1) {
                        starekContext.showError?.("Please select exactly one device to configure.");
                        return;
                    }
                    setConfigWizard({ open: true, device: rows[0] });
                },
                withDivider: true,
            });
        }

        if (canDestroy) {
            actions.push({
                label: "Unbind Device",
                icon: <BiUnlink size={18} />,
                onClick: (rows) => {
                    openConfirm({
                        title: "Unbind selected?",
                        message: DEVICE_MESSAGES.UNBIND_BULK(rows.length),
                        type: "danger",
                        confirmText: "Unbind",
                        count: rows.length,
                        onConfirm: async () => {
                            await doUnbind(rows.map((r) => r.id));
                            setClearSelectionTick((t) => !t);
                            closeConfirm();
                        },
                    });
                },
                withDivider: true,
            });
        }

        if (canUpdateFirmware) {
            actions.push({
                label: "Update firmware",
                icon: <MdSystemUpdateAlt size={18} />,
                onClick: (rows) => {
                    if (rows.length !== 1) {
                        starekContext.showError?.(
                            "Update firmware is allowed for one device at a time.",
                        );
                        return;
                    }
                    openFirmwareModal(rows[0]);
                },
                withDivider: true,
            });
        }

        if (canReboot) {
            actions.push({
                label: "Factory reset",
                icon: <TbRestore size={18} />,
                onClick: (rows) => {
                    openConfirm({
                        title: "Factory reset confirmation?",
                        message: DEVICE_MESSAGES.FACTORY_RESET_BULK(rows.length),
                        type: "danger",
                        confirmText: "Reset",
                        count: rows.length,
                        onConfirm: async () => {
                            await doFactoryReset(rows.map((d) => d.id));
                            closeConfirm();
                        },
                    });
                },
                withDivider: true,
            });
            actions.push({
                label: "Reboot device",
                icon: <BiPowerOff size={18} />,
                danger: true,
                onClick: (rows) => {
                    openConfirm({
                        title: "Reboot confirmation?",
                        message: DEVICE_MESSAGES.REBOOT_BULK(rows.length),
                        confirmText: "Reboot",
                        count: rows.length,
                        onConfirm: async () => {
                            await doReboot(rows.map((d) => d.id));
                            closeConfirm();
                        },
                    });
                },
            });
        }

        if (canWrite && selectedRows.length > 0) {
            actions.push({
                label: "Deactivate",
                icon: "fa-ban",
                danger: true,
                disabled: selectedRows.every((r) => !r.is_active),
                onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
                withDivider: true,
            });
            actions.push({
                label: "Restore",
                icon: "fa-undo",
                disabled: selectedRows.every((r) => r.is_active),
                onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
            });
        }

        return actions;
    }, [underMaintenance, canWrite, canDestroy, canReboot, canUpdateFirmware, selectedRows]);

    const handleToggleActive = async (devices, makeActive) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        const ids = devices.map((d) => d.id);
        try {
            await updateManyShadowDevice({
                variables: {
                    data: makeActive ? restoreData() : softDeleteData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(makeActive ? `Restored ${ids.length} device(s)` : `Deactivated ${ids.length} device(s)`);
            setSelectedRows([]);
            await refetch();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    if (deviceLoading) return <EntityLoadingState icon="fa-microchip" />;

    return (
        <div className="container-fluid">
            {shadowDevices && (
                <>
                    <EntityOperationBar
                        entity={{ singular: "Device", plural: "Devices" }}
                        allowSearch={true}
                        allowAdd={canWrite && !starekContext.hasReachedLimit("devices")}
                        allowDelete={false}
                        allowEdit={false}
                        allowExtendedActions={extendedActions.length > 0}
                        extendedActions={extendedActions}
                        allowShowInactive={true}
                        showInactive={showInactive}
                        onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); }}
                        selectedRows={selectedRows}
                        searchPlaceholder="Search devices…"
                        addButtonText={
                            starekContext.hasReachedLimit("devices")
                                ? "Device Limit Reached"
                                : "Bind Device"
                        }
                        onSearch={(text) => setFilterText(text)}
                        onAdd={initiateBinding}
                    />

                    {!deviceData?.shadowDevices ||
                    deviceData.shadowDevices.length === 0 ? (
                        <EntityEmptyState
                            icon="fa-microchip"
                            entityName="Device"
                            messages={EMPTY_MESSAGES.devices}
                            isFiltered={false}
                            onAdd={initiateBinding}
                            addLabel="Bind a Device"
                        />
                    ) : (
                        <>
                            <DataTable
                                columns={columns}
                                data={filteredItems}
                                selectableRows
                                selectableRowsHighlight
                                onSelectedRowsChange={({ selectedRows }) => {
                                    setSelectedRows(selectedRows);
                                }}
                                clearSelectedRows={clearSelectionTick}
                                keyField="id"
                                pagination
                                highlightOnHover
                                striped
                                responsive
                                defaultSortField="name"
                                defaultSortAsc={true}
                                customStyles={customStyles}
                                expandableRows
                                expandableRowDisabled={(row) =>
                                    row.device.controlledOffline
                                }
                                expandableRowsComponent={({ data }) => (
                                    <div className="tw-w-full tw-bg-gray-50 tw-border-t tw-border-gray-200">
                                        {expandedGraphs[data.id] && (
                                            <div className="tw-p-5">
                                                <div className="tw-flex tw-justify-center tw-items-center tw-min-h-[500px] tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-p-5">
                                                    <div className="tw-w-full tw-h-full">
                                                        <h3 className="tw-text-xl tw-font-bold tw-text-gray-800 tw-mb-4">
                                                            Monitor and Control
                                                        </h3>
                                                        <DeviceGraph
                                                            device={data}
                                                            isExpanded={true}
                                                            sendDeviceCommand={
                                                                sendDeviceCommand
                                                            }
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                                expandableRowExpanded={(row) =>
                                    expandedGraphs[row.id]
                                }
                                onRowExpandToggled={(expanded, row) => {
                                    if (row.device.controlledOffline) {
                                        return; // Prevent expansion for offline devices
                                    }
                                    if (expanded) {
                                        setExpandedGraphs((prev) => {
                                            const newState = {};
                                            Object.keys(prev).forEach((key) => {
                                                newState[key] = false;
                                            });
                                            newState[row.id] = true;
                                            return newState;
                                        });
                                    } else {
                                        setExpandedGraphs((prev) => {
                                            const newState = { ...prev };
                                            newState[row.id] = false;
                                            return newState;
                                        });
                                    }
                                }}
                            />
                        </>
                    )}
                    <DeviceSettingsSidebar
                        isOpen={isSidebarOpen}
                        onClose={() => setIsSidebarOpen(false)}
                        device={selectedDevice}
                    />
                    {selectedLocation && (
                        <LocationPopup
                            locationData={selectedLocation}
                            onClose={handleClosePopup}
                        />
                    )}
                    <ConfirmModal
                        open={confirmCfg.open}
                        type={confirmCfg.type}
                        title={confirmCfg.title}
                        message={confirmCfg.message}
                        count={confirmCfg.count}
                        subMessage={confirmCfg.subMessage}
                        confirmText={confirmCfg.confirmText}
                        onConfirm={confirmCfg.onConfirm}
                        onClose={closeConfirm}
                    />
                    <ScheduleMaintenanceModal
                        open={scheduleCfg.open}
                        onClose={closeSchedule}
                        onSubmit={async (startISO, endISO) => {
                            await doScheduleMaintenance(
                                scheduleCfg.targetIds,
                                startISO,
                                endISO,
                            );
                            closeSchedule();
                        }}
                    />
                    <BindDeviceSidebar
                        workspace={lastUsedWorkspace.name}
                        device={deviceData}
                        isOpen={openBindSidebar}
                        onClose={() => setOpenBindSidebar(false)}
                        onBindingComplete={fetchDevices}
                    />
                    {fwModal.open && fwModal.device && (
                        <UpdateFirmwareModal
                            open={fwModal.open}
                            onClose={() =>
                                setFwModal({ open: false, device: null })
                            }
                            model={fwModal.device?.device?.model}
                            currentVersion={
                                fwModal.device?.device?.firmware_version
                            }
                            currentCode={
                                fwModal.device?.device?.firmware_code || 1
                            }
                            onConfirm={(entry) => {
                                const deviceId = fwModal.device?.id;
                                const currentFw =
                                    fwModal.device?.device?.firmware_version ||
                                    "—";
                                setPendingFw(entry);
                                setFwModal({ open: false, device: null });
                                setTimeout(() => {
                                    openConfirm({
                                        title: "Update Firmware Confirmation?",
                                        message:
                                            "Are you sure you want to update the firmware for the selected device? Device will go offline and will reconnect after the firmware update has completed!",
                                        subMessage: `Device: ${deviceId}\nCurrent: ${currentFw}\nSelected: ${entry.firmware_version}`,

                                        confirmText: "Update",
                                        danger: false,
                                        onConfirm: async () => {
                                            try {
                                                await sendDeviceCommand({
                                                    deviceId,
                                                    command: "update_firmware",
                                                    parameters: {
                                                        firmware_code:
                                                            entry.firmware_code,
                                                        firmware_version:
                                                            entry.firmware_version,
                                                        url: entry.url,
                                                        release_date:
                                                            entry.release_date,
                                                        reason: "portal-initiated",
                                                    },
                                                });
                                                starekContext.showSuccess?.(
                                                    "Firmware update queued.",
                                                );
                                            } catch (e) {
                                                starekContext.showError?.(
                                                    e?.message ||
                                                        "Firmware update failed.",
                                                );
                                            } finally {
                                                setPendingFw(null);
                                                closeConfirm();
                                            }
                                        },
                                    });
                                }, 0);
                            }}
                        />
                    )}
                    
                    {configWizard.open && configWizard.device && (
                        <Modal
                            show={configWizard.open}
                            onHide={() => setConfigWizard({ open: false, device: null })}
                            size="lg"
                            centered
                        >
                            <Modal.Header closeButton>
                                <Modal.Title>Configure Device: {configWizard.device.name || configWizard.device.id}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <AddDeviceConfigWizard
                                    onClose={() => setConfigWizard({ open: false, device: null })}
                                    onFinish={async (manifest) => {
                                        try {
                                            const config = manifest.configuration;
                                            const userId = Number(starekContext.user?.id);
                                            console.log('manifest', manifest)

                                            // 1. If "Create New Configuration" AND "Save as Profile" is selected, create the profile first
                                            if (manifest.config_source === "new" && manifest.new_profile_name) {
                                                await createDeviceProfile({
                                                    variables: {
                                                        data: {
                                                            name: manifest.new_profile_name,
                                                            description: manifest.new_profile_description,
                                                            configuration: config,
                                                            plant: { connect: { id: starekContext.lastUsedPlant.id } },
                                                            created_by: { connect: { id: userId } },
                                                            updated_by: { connect: { id: userId } }
                                                        }
                                                    }
                                                });
                                                starekContext.showSuccess?.("New Device Profile created.");
                                            }

                                            // 2. Update the device parameters
                                            await updateDeviceParameters({
                                                variables: {
                                                    where: { id: configWizard.device.id },
                                                    data: { parameters: config }
                                                }
                                            });

                                            starekContext.showSuccess?.("Device configuration updated.");
                                            setConfigWizard({ open: false, device: null });
                                        } catch (e) {
                                            starekContext.showError?.(e.message || "Failed to update configuration");
                                        }
                                    }}
                                />
                            </Modal.Body>
                        </Modal>
                    )}
                </>
            )}
        </div>
    );
};
